import { useEffect, useRef, useState } from "react";
import { Eye, Loader2, Megaphone, Paperclip, Pause, Play, Send, Share2, Sparkles, Square, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { plur } from "../../lib/localize";

const EMPTY = { subject: "", message: "", subject_en: "", message_en: "", attachments: [], scheduled_at: "", recurring: "",
                imgs: [] };  // illustrations Gemini du haut (0 à 3 — l'une sous l'autre, jamais superposées)

// ISO UTC → valeur d'input datetime-local (heure locale du navigateur)
const isoToLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
// datetime-local → ISO UTC pour l'API
const localInputToIso = (v) => (v ? new Date(v).toISOString() : "");

// Carte de statistique du tableau de bord campagnes
const StatCard = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-surface border border-ivory/10 rounded-xl p-5">
    <p className="text-[11px] uppercase tracking-widest text-ivory/55 mb-1.5">{label}</p>
    <p className="font-serif text-3xl text-gold" data-testid={`${testid}-value`}>{value}</p>
    {sub && <p className="text-[11px] text-ivory/60 mt-1">{sub}</p>}
  </div>
);

const SHARE_URL = "https://mrtrophee.ca";  // domaine de production partagé sur les réseaux
const SOCIALS = [
  { id: "facebook", label: "Facebook", share: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}` },
  { id: "instagram", label: "Instagram", share: null },  // pas de partage web : copier puis coller dans l'app
  { id: "linkedin", label: "LinkedIn", share: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}` },
  { id: "x", label: "X (Twitter)", share: (t) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(t || "")}` },
];

const STATUS_LABELS = {
  brouillon: { label: "Brouillon", cls: "border-ivory/30 text-ivory/60" },
  programmee: { label: "Programmée", cls: "border-blue-400/40 text-blue-300" },
  envoi_en_cours: { label: "Envoi en cours…", cls: "border-gold/40 text-gold" },
  en_pause: { label: "En pause", cls: "border-amber-400/40 text-amber-300" },
  arretee: { label: "Arrêtée", cls: "border-red-400/40 text-red-300" },
  envoyee: { label: "Envoyée", cls: "border-emerald-400/40 text-emerald-300" },
};

export default function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [subsCount, setSubsCount] = useState(0);
  const [unsubCount, setUnsubCount] = useState(0);
  const [editingId, setEditingId] = useState(null);   // null = nouvelle campagne
  const [form, setForm] = useState(EMPTY);
  const [composerOpen, setComposerOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [socialPosts, setSocialPosts] = useState(null);
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialStatus, setSocialStatus] = useState({});
  const [socialResults, setSocialResults] = useState({});
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectForm, setConnectForm] = useState({ network: "facebook", token: "", page_id: "", author_urn: "", app_id: "", app_secret: "" });
  const [occasions, setOccasions] = useState([]);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const loadSocialStatus = async () => {
    try {
      const { data } = await api.get("/admin/social/status");
      setSocialStatus(data);
    } catch { /* silencieux */ }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/campaigns");
      setCampaigns(data);
    } catch { /* silencieux */ }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/admin/newsletter-subscribers")
      .then(({ data }) => {
        setSubsCount(data.filter((s) => !s.unsubscribed).length);
        setUnsubCount(data.filter((s) => s.unsubscribed).length);
      })
      .catch(() => {});
    api.get("/admin/occasions").then(({ data }) => setOccasions(data.filter((o) => o.published))).catch(() => {});
    loadSocialStatus();
  }, []);

  // Préremplit le composeur avec un courriel saisonnier lié à une page d'occasion
  const fromOccasion = (slug) => {
    const o = occasions.find((x) => x.slug === slug);
    if (!o) return;
    setForm((f) => ({
      ...f,
      subject: `${o.title_fr} : c'est le temps de commander !`,
      message: `Bonjour,\n\nVotre événement approche — c'est le moment de commander.\n\n${o.desc_fr}\n\n- Gravure laser personnalisée à petit prix, dans notre atelier de Léry\n- Production rapide : comptez 5 jours ouvrables\n\nDécouvrez la sélection complète : https://mrtrophee.ca/occasions/${o.slug}\n\nCommandez en ligne, ou écrivez-nous sur Messenger : https://m.me/trophee.ca\nUne question ? Répondez à ce courriel ou appelez-nous au 514-565-6665.\n\nL'équipe Mr Trophée`,
    }));
    toast.success(`Courriel prérempli depuis « ${o.title_fr} » — relisez et ajustez avant d'envoyer`);
  };

  // progression en direct pendant un envoi (y compris en pause — pour voir la reprise)
  useEffect(() => {
    if (!campaigns.some((c) => c.status === "envoi_en_cours" || c.status === "en_pause")) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [campaigns]);

  // Pause / reprise / arrêt complet d'un envoi de masse (demande du propriétaire)
  const sendControl = async (id, action) => {
    if (action === "stop" && !window.confirm(
      "Arrêter complètement l'envoi ? Les destinataires restants ne recevront RIEN. Cette action est définitive pour cet envoi.")) return;
    setBusy(`${action}-${id}`);
    try {
      await api.post(`/admin/campaigns/${id}/send-control`, { action });
      toast.success(action === "pause" ? "Envoi mis en pause — reprenez quand vous voulez"
        : action === "resume" ? "Envoi repris"
        : "Arrêt demandé — l'envoi s'arrête immédiatement");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY);
    setAiBrief("");
    setSocialPosts(null);
    setComposerOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ subject: c.subject, message: c.message, subject_en: c.subject_en || "", message_en: c.message_en || "",
              attachments: c.attachments || [], imgs: c.imgs || [],
              scheduled_at: isoToLocalInput(c.scheduled_at || ""), recurring: c.recurring || "" });
    setSocialPosts(c.social_posts || null);
    setSocialResults(c.social_results || {});
    setComposerOpen(true);
  };

  const publishSocial = async (network) => {
    if (!window.confirm(`Publier maintenant sur ${network} ?`)) return;
    setBusy(`pub-${network}`);
    try {
      const { data } = await api.post(`/admin/campaigns/${editingId}/publish-social`, { targets: [network] });
      setSocialResults((r) => ({ ...r, ...data }));
      const res = data[network];
      if (res?.ok) toast.success(`Publié sur ${network}`);
      else toast.error(`${network} : ${res?.error || "échec"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const connectSocial = async () => {
    setBusy("connect");
    try {
      await api.post("/admin/social/connect", connectForm);
      toast.success(`${connectForm.network} connecté`);
      setConnectForm({ network: connectForm.network, token: "", page_id: "", author_urn: "", app_id: "", app_secret: "" });
      setConnectOpen(false);
      loadSocialStatus();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const startGuidedFacebook = async () => {
    setBusy("guided");
    try {
      await api.post("/admin/social/facebook/config",
        { app_id: connectForm.app_id, app_secret: connectForm.app_secret });
      const { data } = await api.get("/admin/social/facebook/start");
      window.open(data.url, "_blank", "noopener,noreferrer");
      toast.info("Autorisez dans l'onglet Facebook puis revenez ici — la connexion est permanente");
      setTimeout(loadSocialStatus, 20000);
      setTimeout(loadSocialStatus, 60000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const generateSocial = async () => {
    if (!editingId) {
      toast.error("Enregistrez d'abord la campagne");
      return;
    }
    setSocialLoading(true);
    try {
      const { data } = await api.post(`/admin/campaigns/${editingId}/social-posts`);
      setSocialPosts(data);
      toast.success("Publications générées pour les 4 réseaux");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSocialLoading(false);
    }
  };

  const copyPost = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Publication ${label} copiée`);
    } catch {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...form, scheduled_at: localInputToIso(form.scheduled_at) };
      if (editingId) {
        await api.patch(`/admin/campaigns/${editingId}`, payload);
        toast.success("Campagne mise à jour");
      } else {
        const { data } = await api.post("/admin/campaigns", payload);
        setEditingId(data.id);
        toast.success(form.scheduled_at ? "Campagne programmée" : "Brouillon enregistré");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const sendNow = async (id) => {
    if (!window.confirm("Envoyer maintenant à tous les abonnés de l'info-lettre ?")) return;
    setBusy(`send-${id}`);
    try {
      const { data } = await api.post(`/admin/campaigns/${id}/send`);
      toast.success(`Envoi lancé vers ${plur(data.recipients, "abonné")}`);
      setComposerOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const sendTest = async (id) => {
    setBusy(`test-${id}`);
    try {
      const { data } = await api.post(`/admin/campaigns/${id}/test`);
      toast.success(`Courriel de test envoyé à ${data.to}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette campagne ?")) return;
    try {
      await api.delete(`/admin/campaigns/${id}`);
      toast.success("Campagne supprimée");
      if (editingId === id) { setComposerOpen(false); setEditingId(null); }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // ILLUSTRATIONS DU HAUT par campagne (demande du propriétaire, 2026-10-27 :
  // « donner le choix de quelle image, ou les utiliser en même temps, sans les
  // superposer ») — dessinées par Gemini ou choisies dans la bibliothèque ;
  // plusieurs = affichées L'UNE SOUS L'AUTRE ; vide = celle de saison
  const [imgLoading, setImgLoading] = useState(false);
  const [galerie, setGalerie] = useState(null);        // null = pas encore chargée
  const [galerieOuverte, setGalerieOuverte] = useState(false);
  const genererImg = async () => {
    if (imgLoading || form.imgs.length >= 3) return;
    setImgLoading(true);
    try {
      const { data } = await api.post("/admin/infolettre/image", { sauvegarder: false });
      setForm((f) => ({ ...f, imgs: [...f.imgs, data.path] }));
      setGalerie(null);  // la bibliothèque se rechargera avec la nouvelle image
      toast.success("Illustration dessinée par Gemini et ajoutée à la campagne.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImgLoading(false); }
  };
  const chargerGalerie = async () => {
    try {
      const { data } = await api.get("/admin/bannieres");
      setGalerie(data.images || []);
    } catch { setGalerie([]); }
  };

  // Aperçu avant envoi : rendu exact du courriel (logo, mise en page, pied légal)
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const preview = async () => {
    if (!form.message.trim()) {
      toast.error("Rédigez le message d'abord");
      return;
    }
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/admin/campaigns/preview", {
        subject: form.subject, message: form.message, attachments: form.attachments,
        subject_en: form.subject_en, message_en: form.message_en, imgs: form.imgs,
      });
      setPreviewHtml(data.html);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPreviewBusy(false);
    }
  };

  const generateAi = async () => {
    if (aiBrief.trim().length < 5) {
      toast.error("Décrivez la campagne en quelques mots d'abord");
      return;
    }
    setAiLoading(true);
    try {
      const { data } = await api.post("/admin/campaigns/ai-generate", { brief: aiBrief });
      setForm((f) => ({ ...f, subject: data.subject, message: data.message }));
      toast.success("Campagne rédigée par l'IA — relisez avant d'envoyer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  // Traduction anglaise IA de la campagne (règle permanente : courriel bilingue FR + EN)
  const [trLoading, setTrLoading] = useState(false);
  const translateEn = async () => {
    if (!form.message.trim()) {
      toast.error("Rédigez le message français d'abord");
      return;
    }
    setTrLoading(true);
    try {
      const { data } = await api.post("/admin/campaigns/translate", {
        subject: form.subject, message: form.message,
      });
      setForm((f) => ({ ...f, subject_en: data.subject_en, message_en: data.message_en }));
      toast.success("Version anglaise traduite — relisez-la avant d'envoyer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTrLoading(false);
    }
  };

  // Copie une campagne précédente en NOUVEAU brouillon (pour de nouveaux envois)
  const duplicate = (c) => {
    setForm({
      subject: `${c.subject} (copie)`.slice(0, 150),
      message: c.message,
      subject_en: c.subject_en || "",
      message_en: c.message_en || "",
      attachments: c.attachments || [],
      scheduled_at: "", recurring: "",
    });
    setEditingId(null);
    setComposerOpen(true);
    toast.success("Campagne copiée en brouillon — ajustez puis enregistrez");
    setTimeout(() => document.querySelector('[data-testid="campaign-composer"]')?.scrollIntoView({ behavior: "smooth" }), 150);
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy("upload");
    try {
      const { data } = await api.post("/admin/campaigns/attachment", fd);
      setForm((f) => ({ ...f, attachments: [...f.attachments, data] }));
      toast.success(`${data.name} joint (lien hébergé dans le courriel)`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  // --- Tableau de bord : agrégats sur les campagnes ayant envoyé au moins un courriel ---
  const sentCamps = campaigns.filter((c) => (c.sent || 0) > 0);
  const totSent = sentCamps.reduce((a, c) => a + (c.sent || 0), 0);
  const totOpens = sentCamps.reduce((a, c) => a + (c.opens || 0), 0);
  const totClicks = sentCamps.reduce((a, c) => a + (c.clicks || 0), 0);

  return (
    <div data-testid="campaigns-tab">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-3xl">Campagnes marketing</h2>
          <p className="text-sm text-ivory/70 mt-1">
            Destinataires : <strong className="text-gold">{subsCount}</strong> abonné(s) de la liste de distribution
            (clients ayant coché la case infolettre à l'inscription).
            {unsubCount > 0 && (
              <span className="text-red-400/90" data-testid="campaigns-unsub-count">
                {" "}· {unsubCount} désabonné{unsubCount > 1 ? "s" : ""} (exclu{unsubCount > 1 ? "s" : ""} des envois — liste dans Clients → Désabonnés)
              </span>
            )}
          </p>
        </div>
        <button data-testid="campaign-new-btn" onClick={openNew}
          className="inline-flex items-center gap-2 bg-gold text-ink px-6 py-3 rounded-full text-sm font-semibold hover:bg-champagne transition-colors duration-300">
          <Megaphone className="w-4 h-4" /> Nouvelle campagne
        </button>
      </div>

      {/* -------- Connexions réseaux sociaux (toujours accessible) -------- */}
      <div className="border border-ivory/15 rounded-lg p-5 mb-8" data-testid="social-connections-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <p className="text-sm font-semibold inline-flex items-center gap-2">
            <Share2 className="w-4 h-4 text-gold" /> Connexions réseaux sociaux
          </p>
          <button data-testid="social-connect-toggle" onClick={() => setConnectOpen((o) => !o)}
            className="text-xs text-gold hover:text-champagne underline underline-offset-2 transition-colors">
            {connectOpen ? "Masquer la connexion API" : "Connecter un réseau (API)"}
          </button>
        </div>
        <div className="flex gap-4 flex-wrap text-xs" data-testid="social-status">
          {SOCIALS.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${socialStatus[s.id] ? "bg-emerald-400" : "bg-ivory/25"}`} />
              {s.label} {socialStatus[s.id] ? `connecté${s.id === "facebook" && socialStatus.facebook_page_name ? ` (${socialStatus.facebook_page_name})` : ""}` : "non connecté"}
            </span>
          ))}
        </div>
        {connectOpen && (
          <div className="border border-gold/25 bg-gold/5 rounded-lg p-4 mt-4" data-testid="social-connect-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <select value={connectForm.network} data-testid="social-connect-network"
                onChange={(e) => setConnectForm((f) => ({ ...f, network: e.target.value }))}
                className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none">
                <option value="facebook">Facebook (connexion guidée OAuth)</option>
                <option value="linkedin">LinkedIn (jeton OAuth)</option>
                <option value="x">X / Twitter (jeton OAuth 2.0)</option>
              </select>
              <input value={connectForm.token} data-testid="social-connect-token" placeholder="Jeton d'accès"
                onChange={(e) => setConnectForm((f) => ({ ...f, token: e.target.value }))}
                className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
              {connectForm.network === "facebook" && (
                <>
                  <input value={connectForm.app_id} data-testid="social-connect-app-id" placeholder="ID de l'app Meta"
                    onChange={(e) => setConnectForm((f) => ({ ...f, app_id: e.target.value }))}
                    className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                  <input value={connectForm.app_secret} data-testid="social-connect-app-secret" placeholder="Clé secrète de l'app Meta"
                    onChange={(e) => setConnectForm((f) => ({ ...f, app_secret: e.target.value }))}
                    className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                  <input value={connectForm.page_id} data-testid="social-connect-page-id" placeholder="ID de la page (manuel seulement)"
                    onChange={(e) => setConnectForm((f) => ({ ...f, page_id: e.target.value }))}
                    className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
                </>
              )}
              {connectForm.network === "linkedin" && (
                <input value={connectForm.author_urn} data-testid="social-connect-author" placeholder="URN auteur (optionnel — déduit du jeton)"
                  onChange={(e) => setConnectForm((f) => ({ ...f, author_urn: e.target.value }))}
                  className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none" />
              )}
            </div>
            {connectForm.network === "facebook" && socialStatus.facebook_redirect_uri && (
              <p className="text-xs text-ivory/70 mt-3">
                Dans votre app Meta (Facebook Login → Paramètres), collez cette URI de redirection OAuth valide :{" "}
                <code className="text-gold/90 break-all">{socialStatus.facebook_redirect_uri}</code>
              </p>
            )}
            <div className="flex items-center gap-3 mt-3 flex-wrap">
              {connectForm.network === "facebook" && (
                <button data-testid="social-guided-facebook" onClick={startGuidedFacebook}
                  disabled={busy === "guided" || !connectForm.app_id || !connectForm.app_secret}
                  className="inline-flex items-center gap-2 bg-gold text-ink px-4 py-1.5 rounded-full text-xs font-semibold hover:bg-champagne transition-colors disabled:opacity-50">
                  {busy === "guided" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Connexion guidée (recommandée — jeton permanent)
                </button>
              )}
              <button data-testid="social-connect-save" onClick={connectSocial} disabled={busy === "connect" || !connectForm.token}
                className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs transition-colors disabled:opacity-50 ${
                  connectForm.network === "facebook"
                    ? "border border-ivory/30 text-ivory/70 hover:border-gold hover:text-gold"
                    : "bg-gold text-ink font-semibold hover:bg-champagne"}`}>
                {busy === "connect" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {connectForm.network === "facebook" ? "Connexion manuelle (jeton temporaire)" : "Enregistrer la connexion"}
              </button>
              <span className="text-xs text-ivory/65">Les jetons sont stockés côté serveur et ne sont jamais affichés.</span>
            </div>
          </div>
        )}
      </div>

      {/* -------- Tableau de bord -------- */}
      {sentCamps.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid="campaign-stats">
          <StatCard label="Abonnés actifs" value={subsCount} testid="stat-subs" />
          <StatCard label="Désabonnés" value={unsubCount} testid="stat-unsub" />
          <StatCard label="Courriels envoyés" value={totSent} sub={plur(sentCamps.length, "campagne")} testid="stat-sent" />
          <StatCard label="Taux d'ouverture" value={`${totSent ? Math.round((totOpens / totSent) * 100) : 0} %`}
            sub={`${plur(totOpens, "ouverture")} au total`} testid="stat-open-rate" />
          <StatCard label="Taux de clic" value={`${totSent ? Math.round((totClicks / totSent) * 100) : 0} %`}
            sub={`${totClicks} clic(s) au total`} testid="stat-click-rate" />
        </div>
      )}

      {/* -------- Liste -------- */}
      <div className="space-y-3 mb-12">
        {campaigns.length === 0 && (
          <p className="text-ivory/65 text-sm" data-testid="campaigns-empty">Aucune campagne pour l'instant.</p>
        )}
        {campaigns.map((c) => {
          const st = STATUS_LABELS[c.status] || STATUS_LABELS.brouillon;
          const enCours = c.status === "envoi_en_cours" || c.status === "en_pause";
          const pct = c.total ? Math.min(100, Math.round(((c.sent + (c.failed || 0)) / c.total) * 100)) : 0;
          return (
            <div key={c.id} data-testid={`campaign-item-${c.id}`}
              className="flex items-center gap-4 border border-ivory/10 rounded-lg px-5 py-4 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <p className="font-medium">{c.subject}</p>
                <p className="text-xs text-ivory/65 mt-0.5">
                  {c.status === "envoyee"
                    ? `Envoyée le ${new Date(c.sent_at).toLocaleString("fr-CA", { timeZone: "America/Montreal" })} — ${c.sent} réussi(s), ${c.failed} échec(s) — ${c.opens ?? 0} ouverture(s) (${c.sent ? Math.round(((c.opens ?? 0) / c.sent) * 100) : 0} %), ${c.clicks ?? 0} clic(s) (${c.sent ? Math.round(((c.clicks ?? 0) / c.sent) * 100) : 0} %)`
                    : c.status === "programmee"
                      ? `Programmée pour le ${new Date(c.scheduled_at).toLocaleString("fr-CA", { timeZone: "America/Montreal" })}${c.recurring === "annuel" ? " — se répète chaque année" : ""}${c.last_sent_at ? ` · dernier envoi le ${new Date(c.last_sent_at).toLocaleDateString("fr-CA", { timeZone: "America/Montreal" })} (${c.sent} réussi(s))` : ""}`
                      : enCours
                        ? `${c.status === "en_pause" ? "EN PAUSE" : "Envoi en cours"} — ${c.sent || 0}/${c.total || "?"} envoyé(s)${c.failed ? ` · ${c.failed} échec(s)` : ""}`
                        : c.status === "arretee"
                          ? `ARRÊTÉE ${c.stopped_at ? `le ${new Date(c.stopped_at).toLocaleString("fr-CA", { timeZone: "America/Montreal" })}` : ""} — ${c.sent || 0} envoyé(s) sur ${c.total || "?"}, ${c.failed || 0} échec(s), ${Math.max(0, (c.total || 0) - (c.sent || 0) - (c.failed || 0))} non envoyé(s)`
                          : `Brouillon — créée le ${new Date(c.created_at).toLocaleString("fr-CA", { timeZone: "America/Montreal" })}`}
                </p>
              </div>
              <span className={`text-xs px-3 py-1 rounded-full border ${st.cls}`} data-testid={`campaign-status-${c.id}`}>{st.label}</span>
              {c.recurring === "annuel" && (
                <span data-testid={`campaign-annual-${c.id}`} className="text-xs px-3 py-1 rounded-full border border-gold/40 text-gold inline-flex items-center gap-1">
                  ↻ Annuelle
                </span>
              )}
              <button data-testid={`campaign-copy-${c.id}`} onClick={() => duplicate(c)}
                title="Copier en nouveau brouillon — réutiliser pour de nouveaux clients"
                className="text-xs text-ivory/60 hover:text-gold transition-colors">Copier</button>
              {/* Contrôle en direct de l'envoi (demande du propriétaire) : pause,
                  reprise ou arrêt complet — lu par la tâche à chaque destinataire */}
              {enCours && (
                <div className="w-full" data-testid={`campaign-progress-${c.id}`}>
                  <div className="h-2 rounded-full bg-ivory/10 overflow-hidden mb-3">
                    <div className={`h-full transition-all duration-500 ${c.status === "en_pause" ? "bg-amber-400" : "bg-gold"}`}
                      data-testid={`campaign-progress-bar-${c.id}`}
                      style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] text-ivory/60 mr-2">{pct} % — {c.sent || 0} envoyé(s){c.failed ? `, ${c.failed} échec(s)` : ""} sur {c.total || "?"}</span>
                    {c.status === "envoi_en_cours" ? (
                      <button data-testid={`campaign-pause-${c.id}`} onClick={() => sendControl(c.id, "pause")}
                        disabled={busy === `pause-${c.id}`}
                        className="inline-flex items-center gap-1.5 text-xs border border-amber-400/50 text-amber-300 px-3 py-1.5 rounded-full hover:bg-amber-400 hover:text-ink transition-colors disabled:opacity-50">
                        {busy === `pause-${c.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pause className="w-3.5 h-3.5" />} Pause
                      </button>
                    ) : (
                      <button data-testid={`campaign-resume-${c.id}`} onClick={() => sendControl(c.id, "resume")}
                        disabled={busy === `resume-${c.id}`}
                        className="inline-flex items-center gap-1.5 text-xs border border-emerald-400/50 text-emerald-300 px-3 py-1.5 rounded-full hover:bg-emerald-400 hover:text-ink transition-colors disabled:opacity-50">
                        {busy === `resume-${c.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Reprendre
                      </button>
                    )}
                    <button data-testid={`campaign-stop-${c.id}`} onClick={() => sendControl(c.id, "stop")}
                      disabled={busy === `stop-${c.id}`}
                      className="inline-flex items-center gap-1.5 text-xs border border-red-400/50 text-red-300 px-3 py-1.5 rounded-full hover:bg-red-400 hover:text-ink transition-colors disabled:opacity-50">
                      {busy === `stop-${c.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />} Arrêter complètement
                    </button>
                  </div>
                </div>
              )}
              {!enCours && c.status !== "envoyee" && (
                <div className="flex gap-3">
                  {c.status !== "arretee" && (
                    <>
                      <button data-testid={`campaign-test-${c.id}`} onClick={() => sendTest(c.id)}
                        disabled={busy === `test-${c.id}`}
                        className="text-xs text-ivory/60 hover:text-gold transition-colors">Test</button>
                      <button data-testid={`campaign-edit-${c.id}`} onClick={() => openEdit(c)}
                        className="text-xs text-ivory/60 hover:text-gold transition-colors">Modifier</button>
                      <button data-testid={`campaign-send-${c.id}`} onClick={() => sendNow(c.id)}
                        disabled={busy === `send-${c.id}`}
                        className="text-xs text-gold hover:text-champagne font-semibold transition-colors">
                        {busy === `send-${c.id}` ? "…" : "Envoyer"}
                      </button>
                    </>
                  )}
                  <button data-testid={`campaign-delete-${c.id}`} onClick={() => remove(c.id)}
                    className="text-xs text-ivory/65 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* -------- Composeur -------- */}
      {composerOpen && (
        <div className="border border-ivory/15 rounded-xl p-6 lg:p-8" data-testid="campaign-composer">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-2xl">{editingId ? "Modifier la campagne" : "Nouvelle campagne"}</h3>
            <button onClick={() => setComposerOpen(false)} className="text-ivory/65 hover:text-gold"><X className="w-5 h-5" /></button>
          </div>

          {/* IA */}
          <div className="border border-gold/25 bg-gold/5 rounded-lg p-5 mb-6">
            <p className="text-sm text-gold font-semibold mb-2 inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Rédaction IA
            </p>
            <div className="flex gap-3 flex-wrap">
              <input
                data-testid="campaign-ai-brief"
                value={aiBrief}
                onChange={(e) => setAiBrief(e.target.value)}
                placeholder="Ex. : annoncer les soldes de fin de saison sur les plaques en cristal…"
                className="flex-1 min-w-[260px] bg-transparent border-b border-ivory/20 focus:border-gold focus:outline-none py-2 text-sm"
              />
              <button data-testid="campaign-ai-btn" onClick={generateAi} disabled={aiLoading}
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2 rounded-full text-sm hover:bg-gold/10 transition-colors disabled:opacity-50">
                {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Rédiger avec l'IA
              </button>
            </div>
            {occasions.length > 0 && (
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gold/15 flex-wrap">
                <label className="text-xs text-ivory/65">Courriel saisonnier depuis une occasion :</label>
                <select defaultValue="" data-testid="campaign-from-occasion"
                  onChange={(e) => { if (e.target.value) { fromOccasion(e.target.value); e.target.value = ""; } }}
                  className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-sm focus:border-gold focus:outline-none">
                  <option value="" disabled>Choisir une occasion…</option>
                  {occasions.map((o) => <option key={o.slug} value={o.slug}>{o.title_fr}</option>)}
                </select>
                <span className="text-[11px] text-ivory/60">Préremplit objet + message avec le lien de la page /occasions/…</span>
              </div>
            )}
          </div>

          <label className="block text-xs tracking-widest uppercase text-ivory/60 mb-2">Objet</label>
          <input data-testid="campaign-subject" value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            className="w-full bg-transparent border-b border-ivory/20 focus:border-gold focus:outline-none py-2 mb-6" />

          <label className="block text-xs tracking-widest uppercase text-ivory/60 mb-2">Message</label>
          <textarea data-testid="campaign-message" rows={10} value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
            className="w-full bg-transparent border border-ivory/20 focus:border-gold focus:outline-none rounded-lg p-4 text-sm leading-relaxed mb-6" />

          {/* ILLUSTRATIONS DU HAUT — choisies par campagne (demande du
              propriétaire, 2026-10-27) : 3 max, affichées L'UNE SOUS L'AUTRE
              dans le courriel, JAMAIS superposées ; vide = illustration de
              saison réglée dans « Envoi à l'info-lettre » */}
          <label className="block text-xs tracking-widest uppercase text-ivory/60 mb-2">
            Illustrations du haut (3 max — une sous l'autre, jamais superposées)
          </label>
          <div className="flex items-center gap-3 flex-wrap mb-2" data-testid="campaign-imgs-section">
            {form.imgs.map((p, i) => (
              <span key={p} className="relative inline-block" title={`Illustration ${i + 1} — la × la retire`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt={`Illustration ${i + 1}`}
                  data-testid={`campaign-img-${i}`}
                  className="h-14 w-24 object-cover rounded-lg border border-gold/40 bg-black" />
                <button onClick={() => setForm((f) => ({ ...f, imgs: f.imgs.filter((_, j) => j !== i) }))}
                  data-testid={`campaign-img-retirer-${i}`}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center hover:bg-red-400 transition-colors">
                  ×
                </button>
              </span>
            ))}
            <button data-testid="campaign-img-generer" onClick={genererImg}
              disabled={imgLoading || form.imgs.length >= 3}
              className="inline-flex items-center gap-2 text-xs border border-gold/40 text-gold px-4 py-1.5 rounded-full hover:bg-gold/10 transition-colors disabled:opacity-50">
              {imgLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Illustrer avec Gemini
            </button>
            <button data-testid="campaign-img-galerie"
              onClick={() => { setGalerieOuverte((v) => !v); if (!galerie) chargerGalerie(); }}
              className="inline-flex items-center gap-2 text-xs border border-ivory/25 text-ivory/65 px-4 py-1.5 rounded-full hover:border-gold/50 hover:text-gold transition-colors">
              <Paperclip className="w-3 h-3" /> Bibliothèque
            </button>
          </div>
          {galerieOuverte && (
            <div className="flex flex-wrap items-center gap-2 mb-2 p-3 border border-ivory/10 rounded-lg" data-testid="campaign-img-galerie-panel">
              {(galerie || []).length === 0 && (
                <span className="text-xs text-ivory/40">Aucune illustration dessinée pour l'instant — « Illustrer avec Gemini » en crée une.</span>
              )}
              {(galerie || []).map((g) => {
                const actif = form.imgs.includes(g.path);
                return (
                  <button key={g.path} type="button"
                    title={actif ? `${g.nom} — dans cette campagne, cliquez pour retirer` : `${g.nom} — cliquez pour ajouter`}
                    onClick={() => setForm((f) => ({ ...f, imgs: actif ? f.imgs.filter((x) => x !== g.path) : (f.imgs.length < 3 ? [...f.imgs, g.path] : f.imgs) }))}
                    data-testid={`campaign-galerie-item-${g.path.slice(-8)}`}
                    className={`rounded-lg overflow-hidden border transition-all ${actif ? "border-gold ring-2 ring-gold/60" : "border-ivory/15 opacity-70 hover:opacity-100"}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${g.url}`} alt={g.nom}
                      className="h-12 w-20 object-cover bg-black" />
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-xs text-ivory/65 mb-6">Vide = l'illustration de saison réglée dans « Envoi à l'info-lettre ». Avec plusieurs images : elles partent l'une sous l'autre en haut du courriel.</p>

          {/* Version anglaise — règle permanente : tout courriel part bilingue FR + EN */}
          <div className="border border-gold/25 bg-gold/5 rounded-lg p-5 mb-6" data-testid="campaign-en-section">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <p className="text-sm font-semibold text-gold inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> Version anglaise (obligatoire — courriel bilingue)
              </p>
              <button data-testid="campaign-translate-btn" onClick={translateEn} disabled={trLoading}
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-1.5 rounded-full text-xs hover:bg-gold/10 transition-colors disabled:opacity-50">
                {trLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Traduire en anglais (IA)
              </button>
            </div>
            <p className="text-xs text-ivory/65 mb-4">Le courriel partira en français PUIS en anglais dans le même message. Laissez vide : la traduction IA sera générée automatiquement à l'envoi.</p>
            <label className="block text-xs tracking-widest uppercase text-ivory/60 mb-2">Objet (anglais)</label>
            <input data-testid="campaign-subject-en" value={form.subject_en}
              onChange={(e) => setForm((f) => ({ ...f, subject_en: e.target.value }))}
              className="w-full bg-transparent border-b border-ivory/20 focus:border-gold focus:outline-none py-2 mb-4" />
            <label className="block text-xs tracking-widest uppercase text-ivory/60 mb-2">Message (anglais)</label>
            <textarea data-testid="campaign-message-en" rows={6} value={form.message_en}
              onChange={(e) => setForm((f) => ({ ...f, message_en: e.target.value }))}
              className="w-full bg-transparent border border-ivory/20 focus:border-gold focus:outline-none rounded-lg p-4 text-sm leading-relaxed" />
          </div>

          {/* Pièces jointes (liens hébergés) */}
          <label className="block text-xs tracking-widest uppercase text-ivory/60 mb-2">Pièces jointes</label>
          <div className="flex items-center gap-3 flex-wrap mb-2">
            {form.attachments.map((a, i) => (
              <span key={a.url} data-testid={`campaign-attachment-${i}`}
                className="inline-flex items-center gap-2 text-xs border border-ivory/20 rounded-full px-3 py-1.5">
                <Paperclip className="w-3 h-3" /> {a.name}
                <button onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((_, j) => j !== i) }))}
                  className="text-ivory/65 hover:text-red-400"><X className="w-3 h-3" /></button>
              </span>
            ))}
            <button data-testid="campaign-attachment-btn" onClick={() => fileRef.current?.click()}
              disabled={busy === "upload" || form.attachments.length >= 5}
              className="inline-flex items-center gap-2 text-xs border border-dashed border-ivory/30 text-ivory/60 px-4 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
              {busy === "upload" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
              Joindre un fichier (5 max)
            </button>
            <input ref={fileRef} type="file" data-testid="campaign-attachment-input" className="hidden" onChange={uploadAttachment} />
          </div>
          <p className="text-xs text-ivory/65 mb-6">Les fichiers sont hébergés sur mrtrophee.ca et joints au courriel sous forme de liens de téléchargement.</p>

          {/* Programmation */}
          <label className="block text-xs tracking-widest uppercase text-ivory/60 mb-2">Programmer l'envoi (optionnel)</label>
          <input data-testid="campaign-schedule" type="datetime-local" value={form.scheduled_at}
            onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            className="bg-transparent border-b border-ivory/20 focus:border-gold focus:outline-none py-2 text-sm mb-4 [color-scheme:dark]" />
          <label className="flex items-center gap-2.5 text-sm text-ivory/70 mb-8 select-none cursor-pointer" data-testid="campaign-recurring-label">
            <input type="checkbox" data-testid="campaign-recurring"
              checked={form.recurring === "annuel"}
              disabled={!form.scheduled_at}
              onChange={(e) => setForm((f) => ({ ...f, recurring: e.target.checked ? "annuel" : "" }))}
              className="w-4 h-4 accent-[#c9a227]" />
            Courriel saisonnier : se répète chaque année à la même date
            <span className="text-xs text-ivory/60">(ex. tournois de hockey chaque 1er mars — nécessite une date programmée)</span>
          </label>

          {/* Réseaux sociaux */}
          {editingId && (
            <div className="border border-ivory/15 rounded-lg p-5 mb-8" data-testid="social-section">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
                <p className="text-sm font-semibold inline-flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-gold" /> Publier sur les réseaux sociaux
                </p>
                <button data-testid="social-generate-btn" onClick={generateSocial} disabled={socialLoading}
                  className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-1.5 rounded-full text-xs hover:bg-gold/10 transition-colors disabled:opacity-50">
                  {socialLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {socialPosts ? "Régénérer les publications" : "Générer les publications IA"}
                </button>
              </div>
              <p className="text-xs text-ivory/65 mb-4">Textes adaptés par réseau, à publier en 2 clics — ou automatiquement pour les réseaux connectés (carte « Connexions » ci-dessus).</p>
              {socialPosts && (
                <div className="grid md:grid-cols-2 gap-4">
                  {SOCIALS.map((s) => {
                    const res = socialResults[s.id];
                    return (
                      <div key={s.id} data-testid={`social-card-${s.id}`} className="border border-ivory/10 rounded-lg p-4">
                        <p className="text-xs tracking-widest uppercase text-gold/80 mb-2">
                          {s.label}
                          {res && <span className={`ml-2 normal-case ${res.ok ? "text-emerald-400" : "text-red-400"}`}>
                            {res.ok ? "— publié ✓" : `— échec : ${(res.error || "").slice(0, 60)}`}
                          </span>}
                        </p>
                        <textarea readOnly rows={5} value={socialPosts[s.id] || ""} data-testid={`social-text-${s.id}`}
                          className="w-full bg-transparent border border-ivory/10 rounded-lg p-3 text-xs leading-relaxed text-ivory/80 mb-3" />
                        <div className="flex gap-2 flex-wrap items-center">
                          <button data-testid={`social-copy-${s.id}`} onClick={() => copyPost(socialPosts[s.id] || "", s.label)}
                            className="text-xs border border-ivory/25 text-ivory/70 px-4 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                            Copier
                          </button>
                          {s.share && (
                            <button data-testid={`social-share-${s.id}`}
                              onClick={() => window.open(s.share(socialPosts[s.id]), "_blank", "noopener,noreferrer")}
                              className="text-xs border border-ivory/25 text-ivory/70 px-4 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                              Ouvrir {s.label}
                            </button>
                          )}
                          {!s.share && <span className="text-xs text-ivory/60 self-center">collez dans l'app mobile</span>}
                          {socialStatus[s.id] && (
                            <button data-testid={`social-publish-${s.id}`} onClick={() => publishSocial(s.id)}
                              disabled={busy === `pub-${s.id}`}
                              className="text-xs bg-gold text-ink font-semibold px-4 py-1.5 rounded-full hover:bg-champagne transition-colors disabled:opacity-50">
                              {busy === `pub-${s.id}` ? "Publication…" : "Publier automatiquement"}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 flex-wrap">
            <button data-testid="campaign-preview-btn" onClick={preview} disabled={previewBusy}
              className="inline-flex items-center gap-2 border border-ivory/30 text-ivory px-6 py-3 rounded-full text-sm hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
              {previewBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Aperçu du courriel
            </button>
            <button data-testid="campaign-save" onClick={save} disabled={busy === "save"}
              className="inline-flex items-center gap-2 border border-ivory/30 text-ivory px-6 py-3 rounded-full text-sm hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
              {busy === "save" && <Loader2 className="w-4 h-4 animate-spin" />}
              {form.scheduled_at ? "Programmer" : "Enregistrer le brouillon"}
            </button>
            {editingId && (
              <>
                <button data-testid="campaign-test-btn" onClick={() => sendTest(editingId)} disabled={busy === `test-${editingId}`}
                  className="inline-flex items-center gap-2 border border-ivory/30 text-ivory px-6 py-3 rounded-full text-sm hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
                  {busy === `test-${editingId}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Envoyer un test (à info@trophee.ca)
                </button>
                <button data-testid="campaign-send-btn" onClick={() => sendNow(editingId)} disabled={busy === `send-${editingId}`}
                  className="inline-flex items-center gap-2 bg-gold text-ink px-6 py-3 rounded-full text-sm font-semibold hover:bg-champagne transition-colors disabled:opacity-50">
                  {busy === `send-${editingId}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Envoyer maintenant ({subsCount} abonnés)
                </button>
              </>
            )}
          </div>
          {!editingId && <p className="text-xs text-ivory/65 mt-3">Enregistrez le brouillon pour débloquer l'envoi de test et l'envoi.</p>}
        </div>
      )}

      {/* Aperçu avant envoi : le courriel tel que reçu par les abonnés */}
      {previewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          data-testid="campaign-preview-modal" onClick={() => setPreviewHtml("")}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
              <p className="text-sm font-semibold text-gray-800">Aperçu du courriel — tel que reçu par les abonnés</p>
              <button onClick={() => setPreviewHtml("")} data-testid="campaign-preview-close"
                className="text-gray-500 hover:text-gray-900 transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <iframe title="Aperçu du courriel" srcDoc={previewHtml} data-testid="campaign-preview-frame"
              className="w-full flex-1 min-h-[60vh] bg-[#f4f4f2]" sandbox="allow-same-origin" />
          </div>
        </div>
      )}
    </div>
  );
}
